import { useEffect, useState, useCallback } from "react";
import { appendUnique, firstPage, listedRows, nextPage, trimPage } from "../../lib/paging";
import { LoadMore } from "./LoadMore";
import { Link, useNavigate } from "react-router";
import { Card, Button, Cluster, Drawer, Input, Select, Stack, EmptyState, useToast } from "@salesapp/ui";
import { crmApi, type Company, type Contact, type DuplicateMatch } from "../../lib/crmApi";
import { reportSaveFailure } from "../../lib/saveNotice";
import { COMPANY_STATUS_OPTIONS as COMPANY_STATUS_HINTS, FIELD_HINTS } from "../../help/fieldHints";
import { opsApi, type Territory } from "../../lib/opsApi";
import { ListWorkspace } from "./RecordWorkspace";
import { CompanyDeleteButton } from "./CompanyDeleteButton";
import { SearchBar, StatusSlicer } from "./SearchBar";
import { StatusBadge } from "./StatusBadge";
import { useAccess } from "../../shell/AccessProvider";
import { CompanyWorkflowSettings } from "./CompanyWorkflowSettings";
import { useCrmHref } from "../../lib/customerUrl";
import { ResponsiveDataTable } from "./ResponsiveDataTable";

function accountBadgeClass(status: string | null | undefined): string {
  if (status === "active") return "crm-badge crm-badge--active";
  if (status === "incomplete") return "crm-badge crm-badge--pending";
  if (status === "prospect") return "crm-badge crm-badge--cold";
  if (status === "dormant") return "crm-badge crm-badge--dormant";
  if (status === "inactive" || status === "merged") return "crm-badge crm-badge--lost";
  return "crm-badge";
}

function personPhone(person: Contact): string {
  return person.phone || person.mobile || "—";
}

export function CompaniesPage({ orgId }: { orgId: string }) {
  const { can } = useAccess();
  const { show } = useToast();
  const crmHref = useCrmHref();
  const canConfigure = can("crm.companies", "configure");
  const canAssignTerritory = can("crm.territories");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contactHits, setContactHits] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateMatch | null>(null);
  const [formData, setFormData] = useState({
    legal_name: "",
    trading_name: "",
    primary_phone: "",
    primary_email: "",
    registration_number: "",
    vat_number: "",
    website: "",
    industry: "",
    territory_id: "",
  });
  const [territories, setTerritories] = useState<Territory[]>([]);
  const navigate = useNavigate();

  // Esc closes the New company form and hands focus back to the button that opened it.
  useEffect(() => {
    if (!showForm) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setShowForm(false);
      document.querySelector<HTMLElement>('[data-tour="new-company"]')?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showForm]);

  useEffect(() => {
    const next = search.trim();
    if (!next) {
      setQuery("");
      return;
    }
    const handle = window.setTimeout(() => setQuery(next), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [data, people, territoryRows] = await Promise.all([
        crmApi.listCompanies(orgId, query || undefined, firstPage(), { withTotal: true }),
        query.length >= 2
          ? crmApi.listContacts(orgId, undefined, query).catch(() => [] as Contact[])
          : Promise.resolve([] as Contact[]),
        opsApi.listTerritories(orgId).then((row) => row.territories).catch(() => [] as Territory[]),
      ]);
      const listed = listedRows(data);
      const page = trimPage(listed.rows);
      setCompanies(page.items);
      setHasMore(page.hasMore);
      setTotal(listed.total);
      setContactHits(people);
      setTerritories(territoryRows);
      setFormData((current) => {
        if (current.territory_id) return current;
        const globalRow = territoryRows.find((item) => item.code === "global") ?? territoryRows[0];
        return globalRow ? { ...current, territory_id: globalRow.id } : current;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load companies");
    } finally {
      setLoading(false);
    }
  }, [orgId, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const listed = listedRows(await crmApi.listCompanies(orgId, query || undefined, nextPage(companies.length), { withTotal: true }));
      const page = trimPage(listed.rows);
      setCompanies((current) => appendUnique(current, page.items));
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more companies");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (
      !formData.legal_name.trim() && !formData.registration_number.trim() &&
      !formData.primary_email.trim() && !formData.website.trim()
    ) {
      setDuplicates(null);
      return;
    }
    const handle = window.setTimeout(() => {
      void crmApi
        .findCompanyDuplicates(orgId, {
          legal_name: formData.legal_name || undefined,
          registration_number: formData.registration_number || undefined,
          vat_number: formData.vat_number || undefined,
          email: formData.primary_email.trim() || undefined,
          website: formData.website.trim() || undefined,
        })
        .then(setDuplicates)
        .catch(() => setDuplicates(null));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [orgId, formData.legal_name, formData.registration_number, formData.vat_number, formData.primary_email, formData.website]);

  const handleCreate = async () => {
    if (!formData.legal_name.trim()) return;
    try {
      const res = await crmApi.createCompany(
        {
          legal_name: formData.legal_name,
          trading_name: formData.trading_name || null,
          primary_phone: formData.primary_phone || null,
          primary_email: formData.primary_email || null,
          registration_number: formData.registration_number || null,
          vat_number: formData.vat_number || null,
          website: formData.website || null,
          industry: formData.industry || null,
          territory_id: canAssignTerritory ? formData.territory_id || null : null,
          account_status: "prospect",
        },
        orgId,
      );
      setShowForm(false);
      show("Saved!", "success");
      navigate(crmHref(`/companies/${res.id}`));
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to create company"));
    }
  };

  const searching = query.length > 0;
  const visibleCompanies = statusFilter ? companies.filter((c) => c.account_status === statusFilter) : companies;

  return (
    <ListWorkspace
      title="Companies"
      actions={
        <Cluster gap={2}>
          {canConfigure ? (
            <Button type="button" variant="secondary" data-tour="company-settings" onClick={() => setSettingsOpen(true)}>
              Company settings
            </Button>
          ) : null}
          <Button data-tour="new-company" onClick={() => setShowForm(!showForm)}>{showForm ? "Cancel" : "New company"}</Button>
        </Cluster>
      }
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search name, email, phone, or address…"
            label="Search companies and contacts"
            hint="Search by company name, email, phone, address, or a contact who works there."
          />
          <StatusSlicer
            value={statusFilter}
            onChange={setStatusFilter}
            options={COMPANY_STATUS_HINTS.map((o) => o.label.toLowerCase())}
            label="Filter by account status"
            optionHints={COMPANY_STATUS_HINTS}
          />
        </div>
      }
    >
      {error && <Card className="crm-error">{error}</Card>}

      {contactHits.length > 0 && (
        <Card className="crm-contact-hits">
          <h2 className="crm-section-title">Matching people</h2>
          <table className="crm-table">
            <thead>
              <tr>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
              </tr>
            </thead>
            <tbody>
              {contactHits.map((person) => (
                <tr key={person.id}>
                  <td>
                    {person.first_name} {person.last_name}
                  </td>
                  <td>{person.work_email ?? person.email ?? "—"}</td>
                  <td>{personPhone(person)}</td>
                  <td>
                    {person.company_id ? (
                      <Link to={crmHref(`/companies/${person.company_id}`)}>
                        {person.company_name ?? "Open company"}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {showForm && (
        <Card>
          <Stack gap={3}>
            <Input label="Legal name *" tooltip={FIELD_HINTS.company_legal_name} data-tour="company-legal-name" value={formData.legal_name} onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })} />
            <div className="crm-form-actions">
              <Button data-tour="create-company" onClick={() => handleCreate()} disabled={!formData.legal_name.trim()}>
                Create company
              </Button>
            </div>
            <Input label="Trading name" tooltip={FIELD_HINTS.company_trading_name} value={formData.trading_name} onChange={(e) => setFormData({ ...formData, trading_name: e.target.value })} />
            <Input label="Phone" tooltip={FIELD_HINTS.company_phone} value={formData.primary_phone} onChange={(e) => setFormData({ ...formData, primary_phone: e.target.value })} />
            <Input label="Email" tooltip={FIELD_HINTS.company_email} value={formData.primary_email} onChange={(e) => setFormData({ ...formData, primary_email: e.target.value })} />
            <Input label="Registration number" tooltip={FIELD_HINTS.company_registration} value={formData.registration_number} onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })} />
            <Input label="Tax / VAT number" tooltip={FIELD_HINTS.company_vat} value={formData.vat_number} onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })} />
            <Input label="Website" tooltip={FIELD_HINTS.company_website} value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
            <Input label="Industry" tooltip={FIELD_HINTS.company_industry} value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} />
            {canAssignTerritory && territories.length > 0 && (
              <Select
                label="Territory"
                tooltip="Sales territory this account belongs to — used for ownership and reporting."
                value={formData.territory_id}
                onChange={(event) => setFormData({ ...formData, territory_id: event.target.value })}
              >
                {territories.map((territory) => (
                  <option key={territory.id} value={territory.id}>{territory.name}</option>
                ))}
              </Select>
            )}
            {duplicates && duplicates.matches.length > 0 && (
              <p className="crm-warning">
                Possible duplicates (same name, registration, VAT, email or website): {duplicates.matches.map((item) => item.legal_name).join(", ")}. You can still create it.
              </p>
            )}
          </Stack>
        </Card>
      )}

      {loading ? (
        <Card>Loading...</Card>
      ) : visibleCompanies.length === 0 && (!showForm || searching || statusFilter) ? (
        <Card>
          <EmptyState
            title={searching || statusFilter ? "No matches" : "No companies yet"}
            description={
              searching || statusFilter
                ? "Try a different status, or a company or contact name, email, phone, or address."
                : "Create a company, or capture a lead and an incomplete company will be created for you."
            }
            action={!searching && !statusFilter ? <Button onClick={() => setShowForm(true)}>Create company</Button> : undefined}
          />
        </Card>
      ) : (
        <Card className="crm-table-card">
          <ResponsiveDataTable
            caption="Companies"
            columns={[
              { key: "code", label: "Code", hint: FIELD_HINTS.company_code },
              { key: "name", label: "Name", primary: true, hint: FIELD_HINTS.company_legal_name },
              { key: "industry", label: "Industry", hint: FIELD_HINTS.company_industry },
              { key: "status", label: "Status", primary: true, hint: FIELD_HINTS.company_status },
              { key: "territory", label: "Territory", primary: true, hint: "Sales territory this account belongs to." },
              { key: "phone", label: "Phone", hint: FIELD_HINTS.company_phone },
              { key: "email", label: "Email", hint: FIELD_HINTS.company_email },
            ]}
            rows={visibleCompanies.map((c) => ({
              id: c.id,
              href: crmHref(`/companies/${c.id}`),
              cells: {
                code: c.company_code ?? "—",
                name: <Link to={crmHref(`/companies/${c.id}`)}>{c.legal_name}</Link>,
                industry: c.industry ?? "—",
                status: (
                  <StatusBadge
                    status={c.account_status}
                    options={COMPANY_STATUS_HINTS}
                    className={accountBadgeClass(c.account_status)}
                  />
                ),
                territory: c.territory_name ?? "Global",
                phone: c.primary_phone ?? "—",
                email: c.primary_email ?? "—",
              },
              actions: (
                <Cluster gap={2}>
                  {can("crm.companies", "update") ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(crmHref(`/companies/${c.id}?edit=1`))}
                    >
                      Edit
                    </Button>
                  ) : null}
                  <CompanyDeleteButton
                    orgId={orgId}
                    companyId={c.id}
                    recordLabel={c.legal_name}
                    size="sm"
                    onDeleted={() => load()}
                  />
                </Cluster>
              ),
            }))}
          />
        </Card>
      )}
      <LoadMore shown={companies.length} total={total} hasMore={hasMore} loading={loadingMore} onLoadMore={() => void loadMore()} noun="companies" />
      <Drawer isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} title="Company settings">
        <CompanyWorkflowSettings orgId={orgId} />
      </Drawer>
    </ListWorkspace>
  );
}
